import { Col, Row } from "react-flexbox-grid";
import intl from "react-intl-universal";
import { formatNumber } from "utils/format";

import * as S from "./styled";

enum PolicyStatus {
  Ok = 0,
  Over = 1,
  Out = 2,
}

interface IPolicyData {
  currentStock?: number | null;
  currentStockUnit?: string | null;
  currentStockDoi?: number | null;
  targetPolicy?: number | null;
  targetPolicyUnit?: string | null;
  targetPolicyDoi?: number | null;
  maximumStock?: number | null;
  minimumStock?: number | null;
  maximumStockDoi?: number | null;
  minimumStockDoi?: number | null;
  policyStatus?: PolicyStatus | number | null;
}

interface IProps {
  data: IPolicyData;
}

const STATUS_CLASS_MAP: Record<PolicyStatus, string> = {
  [PolicyStatus.Ok]: "status-on-target",
  [PolicyStatus.Over]: "status-above-target",
  [PolicyStatus.Out]: "status-below-target",
};

const STATUS_I18N_MAP: Record<PolicyStatus, { key: string; fallback: string }> = {
  [PolicyStatus.Ok]: {
    key: "stocks.stockDetail.inventoryPolicy.status.onTarget",
    fallback: "On Target",
  },
  [PolicyStatus.Over]: {
    key: "stocks.stockDetail.inventoryPolicy.status.aboveTarget",
    fallback: "Above Target",
  },
  [PolicyStatus.Out]: {
    key: "stocks.stockDetail.inventoryPolicy.status.belowTarget",
    fallback: "Below Target",
  },
};

const tr = (key: string, fallback: string): string =>
  intl.get(key).defaultMessage(fallback) || fallback;

const formatValue = (value?: number | null, unit?: string | null): string => {
  if (value === null || value === undefined) return "-";
  const formatted = formatNumber(value);
  return unit ? `${formatted} ${unit}` : formatted;
};

const formatDoi = (doi?: number | null): string | null => {
  if (doi === null || doi === undefined) return null;
  return `${formatNumber(doi)} ${tr("stocks.stockDetail.inventoryPolicy.doi", "DOI")}`;
};

const InventoryPolicyBoxes = ({ data }: IProps) => {
  const statusKey = data.policyStatus as PolicyStatus;
  const isKnownStatus =
    statusKey === PolicyStatus.Ok ||
    statusKey === PolicyStatus.Over ||
    statusKey === PolicyStatus.Out;
  const statusClass = isKnownStatus ? STATUS_CLASS_MAP[statusKey] : "";
  const statusEntry = isKnownStatus ? STATUS_I18N_MAP[statusKey] : undefined;
  const statusLabel = statusEntry ? tr(statusEntry.key, statusEntry.fallback) : "-";

  return (
    <S.Wrapper>
      {/* @ts-ignore */}
      <Row>
        {/* @ts-ignore */}
        <Col className="inventory-policy-box">
          <S.Box className="current-stock">
            <span className="box-label">
              {tr("stocks.stockDetail.inventoryPolicy.currentStock", "Current Stock")}
            </span>
            <span className="box-value">
              {formatValue(data.currentStock, data.currentStockUnit)}
            </span>
            {data.currentStockDoi !== null && data.currentStockDoi !== undefined && (
              <span className="box-doi">{formatDoi(data.currentStockDoi)}</span>
            )}
          </S.Box>
        </Col>
        {/* @ts-ignore */}
        <Col className="inventory-policy-box">
          <S.Box className="target-policy">
            <span className="box-label">
              {tr("stocks.stockDetail.inventoryPolicy.targetPolicy", "Target Policy")}
            </span>
            <span className="box-value">
              {formatValue(data.targetPolicy, data.targetPolicyUnit)}
            </span>
            {data.targetPolicyDoi !== null && data.targetPolicyDoi !== undefined && (
              <span className="box-doi">{formatDoi(data.targetPolicyDoi)}</span>
            )}
          </S.Box>
        </Col>
        {/* @ts-ignore */}
        <Col className="inventory-policy-box">
          <S.Box className="minimum-stock">
            <span className="box-label">
              {tr("stocks.stockDetail.inventoryPolicy.minimumStock", "Min")}
            </span>
            <span className="box-value">
              {formatValue(data.minimumStock, data.targetPolicyUnit)}
            </span>
            {data.minimumStock !== null && data.minimumStock !== undefined && (
              <span className="box-doi">{formatDoi(data.minimumStockDoi)}</span>
            )}
          </S.Box>
        </Col>
        {/* @ts-ignore */}
        <Col className="inventory-policy-box">
          <S.Box className="maximum-stock">
            <span className="box-label">
              {tr("stocks.stockDetail.inventoryPolicy.maximumStock", "Max")}
            </span>
            <span className="box-value">
              {formatValue(data.maximumStock, data.targetPolicyUnit)}
            </span>
            {data.maximumStock !== null && data.maximumStock !== undefined && (
              <span className="box-doi">{formatDoi(data.maximumStockDoi)}</span>
            )}
          </S.Box>
        </Col>
        {/* @ts-ignore */}
        <Col className="inventory-policy-box">
          <S.Box className="policy-status">
            <span className="box-label">
              {tr("stocks.stockDetail.inventoryPolicy.policyStatus", "Policy Status")}
            </span>
            <span className={`box-value ${statusClass}`}>{statusLabel}</span>
          </S.Box>
        </Col>
      </Row>
    </S.Wrapper>
  );
};

export default InventoryPolicyBoxes;
